import type { PageOopsProps } from './const';
import { useEffect } from 'react';
import { cn } from '../../utils';
import Button from '../Button';
import './index.less';

function PageOops(props: PageOopsProps) {
  const {
    className,
    style,
    error,
    errorInfo,
    title = 'Something went wrong',
    description = 'Sorry, the page encountered some issues. Please try refreshing the page.',
    refreshButtonText = 'Refresh Page',
    copyButtonText = 'Copy Error Info',
    debugTitle = 'Development Environment Error Details:',
    componentStackTitle = 'Component Stack:',
    onReload,
    onError,
    showDebug = typeof process !== 'undefined' && process.env.NODE_ENV === 'development',
  } = props;

  const handleReload = () => {
    if (onReload) {
      onReload();
    } else {
      window.location.reload();
    }
  };

  const handleCopyError = () => {
    if (!error) {
      return;
    }

    const errorMessage = `${error}`;
    const stackLines = errorInfo?.componentStack?.split('\n').slice(0, 5) || [];
    const copyText = [errorMessage, ...stackLines].join('\n');

    navigator.clipboard.writeText(copyText);
  };

  useEffect(() => {
    if (error && onError) {
      onError(error, errorInfo);
    }
  }, [error, errorInfo, onError]);

  return (
    <div className={cn('wg-page-oops', className)} style={style}>
      <div className="wg-page-oops__icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="120"
          height="120"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>

      <div className="wg-page-oops__title">{title}</div>

      <div className="wg-page-oops__text">{description}</div>

      <Button className="wg-page-oops__button" onClick={handleReload}>
        {refreshButtonText}
      </Button>

      {showDebug && error && (
        <Button className="wg-page-oops__copy-button" onClick={handleCopyError}>
          {copyButtonText}
        </Button>
      )}

      {showDebug && error && (
        <div className="wg-page-oops__debug">
          <div>{debugTitle}</div>
          <div>{`${error}`}</div>
          {errorInfo?.componentStack && (
            <>
              <div style={{ marginTop: '1rem' }}>{componentStackTitle}</div>
              {errorInfo.componentStack.split('\n').map((item, index) => (
                <div key={index}>{item}</div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PageOops;
export type { PageOopsProps };
